/**
 * System messages — operator-authored banners and the maintenance switch, both
 * read from a single JSON file on S3.
 *
 * Deliberately dependency-free: no Angular, no `api.ts` (that wrapper prefixes
 * the API base URL and attaches a Bearer token, neither of which applies to an
 * anonymous absolute URL). Plain `fetch`, mirroring the features.json fetch in
 * `app/main.js`.
 *
 * The file is fetched once before Angular bootstraps and cached on
 * `window.__systemMessages`, so the React banner and the Angular notifications
 * page share one request.
 */

import type { AlertType } from '../components/AlertBlock';

export const SYSTEM_MESSAGES_URL = 'https://iio-assets.s3.amazonaws.com/messages/messages.json';

/** Never let a slow bucket delay app bootstrap. */
export const FETCH_TIMEOUT_MS = 1500;

const ALERT_TYPES: AlertType[] = ['info', 'warning', 'danger', 'success'];

export interface SystemMessage {
  id: string;
  type: AlertType;
  /** Environments this applies to. Omitted/undefined = all environments. */
  envs?: string[];
  title?: string;
  content: string;
  start?: string;
  end?: string;
}

export interface MaintenanceConfig {
  active: boolean;
  /** Environments this applies to. Omitted/undefined = all environments. */
  envs?: string[];
  title?: string;
  content?: string;
  /**
   * Cosmetic only — the time shown to users as when we expect to be back. It
   * deliberately does NOT expire the outage: if the upgrade overruns, the site
   * must stay down rather than let users onto a half-migrated database. Unlike
   * a message's `end`, `active` is the only switch.
   */
  expectedEnd?: string;
}

export interface SystemMessagesFile {
  messages: SystemMessage[];
  maintenance: MaintenanceConfig | null;
}

const emptySystemMessages = (): SystemMessagesFile => ({ messages: [], maintenance: null });

/** The build's environment, matching the values in `env/.env.*`. */
export function currentEnvironment(): string {
  return import.meta.env.VITE_ENVIRONMENT || 'prod';
}

function normalizeEnv(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Environment scoping. An omitted `envs` means "everywhere" so existing entries
 * keep working; an empty array means "nowhere", which parks an entry without
 * deleting it.
 */
export function matchesEnvironment(envs: string[] | undefined, env: string = currentEnvironment()): boolean {
  if (!Array.isArray(envs)) return true;
  const target = normalizeEnv(env);
  return envs.some((e) => normalizeEnv(e) === target);
}

/** Invalid/absent dates are treated as unbounded rather than as "never". */
function withinWindow(start: string | undefined, end: string | undefined, now: Date): boolean {
  if (start) {
    const from = new Date(start);
    if (!isNaN(from.getTime()) && now < from) return false;
  }
  if (end) {
    const to = new Date(end);
    if (!isNaN(to.getTime()) && now > to) return false;
  }
  return true;
}

function parseEnvs(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((e): e is string => typeof e === 'string');
}

function parseMessage(raw: any, index: number): SystemMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const content = typeof raw.content === 'string' ? raw.content : '';
  const title = typeof raw.title === 'string' ? raw.title : undefined;
  if (!content && !title) return null;

  const type: AlertType = ALERT_TYPES.includes(raw.type) ? raw.type : 'info';

  return {
    // A synthesized id keeps dismissal working for entries authored without one.
    id: typeof raw.id === 'string' && raw.id ? raw.id : `msg-${index}`,
    type,
    envs: parseEnvs(raw.envs),
    title,
    content,
    start: typeof raw.start === 'string' ? raw.start : undefined,
    end: typeof raw.end === 'string' ? raw.end : undefined,
  };
}

function parseMaintenance(raw: any): MaintenanceConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    active: raw.active === true,
    envs: parseEnvs(raw.envs),
    title: typeof raw.title === 'string' ? raw.title : undefined,
    content: typeof raw.content === 'string' ? raw.content : undefined,
    expectedEnd: typeof raw.expectedEnd === 'string' ? raw.expectedEnd : undefined,
  };
}

/** Tolerates any garbage — a malformed file must never break the app. */
export function parseSystemMessages(raw: unknown): SystemMessagesFile {
  if (!raw || typeof raw !== 'object') return emptySystemMessages();
  const data = raw as any;
  const messages = Array.isArray(data.messages)
    ? data.messages.map(parseMessage).filter((m: SystemMessage | null): m is SystemMessage => m !== null)
    : [];
  return { messages, maintenance: parseMaintenance(data.maintenance) };
}

export async function loadSystemMessages(url: string = SYSTEM_MESSAGES_URL): Promise<SystemMessagesFile> {
  try {
    const response = await fetch(url, { cache: 'no-cache', signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
    if (!response.ok) return emptySystemMessages();
    return parseSystemMessages(await response.json());
  } catch {
    return emptySystemMessages();
  }
}

/** The file cached by main.js before bootstrap. */
export function getSystemMessagesFile(): SystemMessagesFile {
  return (window as any).__systemMessages || emptySystemMessages();
}

/**
 * Every message whose date window contains `now` and whose `envs` includes the
 * current environment, in file order.
 */
export function getActiveMessages(
  file: SystemMessagesFile = getSystemMessagesFile(),
  now: Date = new Date(),
  env: string = currentEnvironment()
): SystemMessage[] {
  return file.messages.filter(
    (m) => matchesEnvironment(m.envs, env) && withinWindow(m.start, m.end, now)
  );
}

/**
 * Blue/green staging slot. During a cutover prod shows the outage page while this
 * host stays reachable so the new build can be verified against live data.
 */
export const MAINTENANCE_BYPASS_SUBDOMAINS = ['app-temp', 'localhost'];

/** Matches the first host label only, so `app-temp2`/`myapp-temp` do not bypass. */
export function isMaintenanceBypassHost(hostname: string = window.location.hostname): boolean {
  return MAINTENANCE_BYPASS_SUBDOMAINS.includes(hostname.split('.')[0].toLowerCase());
}

/** True when the flag is set and the current environment is targeted. No date check — see `expectedEnd`. */
export function isMaintenanceActive(
  file: SystemMessagesFile = getSystemMessagesFile(),
  env: string = currentEnvironment()
): boolean {
  const m = file.maintenance;
  if (!m || !m.active) return false;
  return matchesEnvironment(m.envs, env);
}

export function getMaintenanceMessage(
  file: SystemMessagesFile = getSystemMessagesFile()
): MaintenanceConfig | null {
  return file.maintenance;
}
